import { useCallback, useEffect, useRef } from 'react';

interface UseBlockSettingsStateOptions {
  open: boolean;
  currentValue: Record<string, unknown> | null;
  mediaKey?: string;
  applyChange: (value: Record<string, unknown> | null, commit: boolean) => void;
  cancelPreview?: () => void;
}

export function useBlockSettingsState({
  open,
  currentValue,
  mediaKey,
  applyChange,
  cancelPreview,
}: UseBlockSettingsStateOptions) {
  const baselineRef = useRef<Record<string, unknown> | null | undefined>(undefined);
  const hasUnpersistedChangesRef = useRef(false);
  const cancelPreviewRef = useRef(cancelPreview);
  const unmountGenerationRef = useRef(0);
  cancelPreviewRef.current = cancelPreview;

  useEffect(() => {
    if (open) {
      if (baselineRef.current === undefined) {
        baselineRef.current = currentValue === null ? null : { ...currentValue };
        hasUnpersistedChangesRef.current = false;
      }
    } else {
      if (hasUnpersistedChangesRef.current) {
        cancelPreview?.();
      }
      baselineRef.current = undefined;
      hasUnpersistedChangesRef.current = false;
    }
  }, [open, currentValue, cancelPreview]);

  useEffect(() => {
    const generation = unmountGenerationRef.current + 1;
    unmountGenerationRef.current = generation;

    return () => {
      // React Strict Mode disconnects and reconnects effects when a keyed child moves.
      // A real unmount has no reconnect to supersede this generation.
      queueMicrotask(() => {
        if (unmountGenerationRef.current !== generation) return;
        if (hasUnpersistedChangesRef.current) {
          cancelPreviewRef.current?.();
        }
      });
    };
  }, []);

  const persistMedia = useCallback(
    (patch: Record<string, unknown>) => {
      const baseline =
        baselineRef.current === undefined ? (currentValue ?? {}) : (baselineRef.current ?? {});
      const hasUnpersistedChanges = hasUnpersistedChangesRef.current;
      if (hasUnpersistedChanges && baselineRef.current !== undefined) {
        applyChange(baselineRef.current, false);
      }

      const committed = { ...baseline, ...patch };
      applyChange(committed, true);
      baselineRef.current = committed;
      if (hasUnpersistedChanges) {
        applyChange({ ...currentValue, ...patch }, false);
      }
    },
    [currentValue, applyChange],
  );

  const previewMediaDelete = useCallback(() => {
    if (mediaKey) {
      const rest = { ...(currentValue ?? {}) };
      delete rest[mediaKey];
      applyChange(rest, false);
    } else {
      applyChange(null, false);
    }
    hasUnpersistedChangesRef.current = true;
  }, [currentValue, mediaKey, applyChange]);

  const applyPreview = useCallback(
    (patch: Record<string, unknown>) => {
      applyChange({ ...(currentValue ?? {}), ...patch }, false);
      hasUnpersistedChangesRef.current = true;
    },
    [currentValue, applyChange],
  );

  const handleSave = useCallback(
    (patch?: Record<string, unknown>) => {
      const hasUnpersistedChanges = hasUnpersistedChangesRef.current;
      if (hasUnpersistedChanges) {
        if (baselineRef.current !== undefined) {
          applyChange(baselineRef.current, false);
        }
      }
      if (hasUnpersistedChanges || patch !== undefined) {
        const committed =
          patch === undefined ? currentValue : { ...(currentValue ?? {}), ...patch };
        applyChange(committed, true);
      }
      hasUnpersistedChangesRef.current = false;
    },
    [currentValue, applyChange],
  );

  const handleCancel = useCallback(() => {
    if (hasUnpersistedChangesRef.current) {
      if (cancelPreview) {
        cancelPreview();
      } else if (baselineRef.current !== undefined) {
        applyChange(baselineRef.current, false);
      }
    }
    hasUnpersistedChangesRef.current = false;
  }, [applyChange, cancelPreview]);

  return { persistMedia, previewMediaDelete, applyPreview, handleSave, handleCancel };
}
